<template>
  <div class="page-container">
    <div class="page-header">
      <div class="header-left">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <!-- 報表類型選擇和篩選 -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-item">
          <label>報表類型</label>
          <select v-model="selectedReportType" @change="handleReportTypeChange" class="form-control">
            <option value="">請選擇報表類型</option>
            <option value="monthly_revenue">月營收報表</option>
            <option value="payment_status">客戶付費狀態統計</option>
            <option value="renewal_rate">訂閱續約率分析</option>
            <option value="aging_analysis">應收帳款帳齡分析</option>
            <option value="payment_method_distribution">付款方式分布統計</option>
          </select>
        </div>
        <div class="filter-item" v-if="needsDateRange">
          <label>開始日期</label>
          <input
            v-model="filters.startDate"
            type="date"
            class="form-control"
            @change="handleFilterChange"
          />
        </div>
        <div class="filter-item" v-if="needsDateRange">
          <label>結束日期</label>
          <input
            v-model="filters.endDate"
            type="date"
            class="form-control"
            @change="handleFilterChange"
          />
        </div>
        <div class="filter-item" v-if="needsSingleDate">
          <label>統計日期</label>
          <input
            v-model="filters.date"
            type="date"
            class="form-control"
            @change="handleFilterChange"
          />
        </div>
        <div class="filter-item" v-if="isSystemAccount">
          <label>平台</label>
          <select v-model="filters.platformId" @change="handleFilterChange" class="form-control">
            <option value="">全部平台</option>
            <option v-for="platform in platforms" :key="platform.id" :value="platform.id">
              {{ platform.name }}
            </option>
          </select>
        </div>
        <div class="filter-item">
          <label>&nbsp;</label>
          <button class="btn btn-primary" @click="fetchReport" :disabled="loading || !selectedReportType">
            查詢
          </button>
        </div>
        <div class="filter-item" v-if="reportData">
          <label>&nbsp;</label>
          <div class="export-buttons">
            <button class="btn btn-secondary" @click="exportToExcel" :disabled="loading">
              <i class="ri-file-excel-line"></i>
              匯出 Excel
            </button>
            <button class="btn btn-secondary" @click="exportToPDF" :disabled="loading">
              <i class="ri-file-pdf-line"></i>
              匯出 PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchReport">重試</button>
    </div>

    <div v-else-if="reportData" class="report-content">
      <!-- 月營收報表 -->
      <div v-if="selectedReportType === 'monthly_revenue'" class="report-section">
        <h2 class="report-title">月營收報表</h2>
        <div class="report-summary">
          <div class="summary-item">
            <span class="summary-label">總營收：</span>
            <span class="summary-value">NT$ {{ formatCurrency(reportData.summary?.totalRevenue || 0) }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">總付款筆數：</span>
            <span class="summary-value">{{ reportData.summary?.totalPaymentCount || 0 }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">平均金額：</span>
            <span class="summary-value">NT$ {{ formatCurrency(reportData.summary?.averageAmount || 0) }}</span>
          </div>
        </div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>月份</th>
                <th>總營收</th>
                <th>付款筆數</th>
                <th>平均金額</th>
                <th>人工收款金額</th>
                <th>綠界扣款金額</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in reportData.monthlyData" :key="item.month">
                <td>{{ item.month }}</td>
                <td>NT$ {{ formatCurrency(item.totalRevenue) }}</td>
                <td>{{ item.paymentCount }}</td>
                <td>NT$ {{ formatCurrency(item.averageAmount) }}</td>
                <td>NT$ {{ formatCurrency(item.manualAmount) }}</td>
                <td>NT$ {{ formatCurrency(item.ecpayAmount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 客戶付費狀態統計 -->
      <div v-if="selectedReportType === 'payment_status'" class="report-section">
        <h2 class="report-title">客戶付費狀態統計</h2>
        <div class="status-grid">
          <div class="status-card">
            <div class="status-label">啟用中</div>
            <div class="status-value">{{ reportData.statusBreakdown?.active?.count || 0 }} 客戶</div>
            <div class="status-amount">NT$ {{ formatCurrency(reportData.statusBreakdown?.active?.totalAmount || 0) }}</div>
          </div>
          <div class="status-card">
            <div class="status-label">寬限期</div>
            <div class="status-value">{{ reportData.statusBreakdown?.gracePeriod?.count || 0 }} 客戶</div>
            <div class="status-amount">NT$ {{ formatCurrency(reportData.statusBreakdown?.gracePeriod?.totalAmount || 0) }}</div>
          </div>
          <div class="status-card">
            <div class="status-label">已過期</div>
            <div class="status-value">{{ reportData.statusBreakdown?.expired?.count || 0 }} 客戶</div>
            <div class="status-amount">NT$ {{ formatCurrency(reportData.statusBreakdown?.expired?.totalAmount || 0) }}</div>
          </div>
        </div>
      </div>

      <!-- 訂閱續約率分析 -->
      <div v-if="selectedReportType === 'renewal_rate'" class="report-section">
        <h2 class="report-title">訂閱續約率分析</h2>
        <div class="report-summary">
          <div class="summary-item">
            <span class="summary-label">續約率：</span>
            <span class="summary-value">{{ ((reportData.renewalRate || 0) * 100).toFixed(2) }}%</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">流失率：</span>
            <span class="summary-value">{{ ((reportData.churnRate || 0) * 100).toFixed(2) }}%</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">到期客戶數：</span>
            <span class="summary-value">{{ reportData.totalExpired || 0 }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">續約客戶數：</span>
            <span class="summary-value">{{ reportData.totalRenewed || 0 }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">流失客戶數：</span>
            <span class="summary-value">{{ reportData.totalChurned || 0 }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">續約金額：</span>
            <span class="summary-value">NT$ {{ formatCurrency(reportData.renewalAmount || 0) }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">流失金額：</span>
            <span class="summary-value">NT$ {{ formatCurrency(reportData.churnAmount || 0) }}</span>
          </div>
        </div>
      </div>

      <!-- 應收帳款帳齡分析 -->
      <div v-if="selectedReportType === 'aging_analysis'" class="report-section">
        <h2 class="report-title">應收帳款帳齡分析</h2>
        <div class="aging-grid">
          <div class="aging-card" v-for="(item, key) in reportData.agingBreakdown" :key="key">
            <div class="aging-label">{{ key }} 天</div>
            <div class="aging-value">{{ item.count || 0 }} 客戶</div>
            <div class="aging-amount">NT$ {{ formatCurrency(item.totalAmount || 0) }}</div>
          </div>
        </div>
      </div>

      <!-- 付款方式分布統計 -->
      <div v-if="selectedReportType === 'payment_method_distribution'" class="report-section">
        <h2 class="report-title">付款方式分布統計</h2>
        <div class="distribution-grid">
          <div class="distribution-card">
            <div class="distribution-label">人工收款</div>
            <div class="distribution-value">{{ reportData.distribution?.manual?.count || 0 }} 筆</div>
            <div class="distribution-amount">NT$ {{ formatCurrency(reportData.distribution?.manual?.totalAmount || 0) }}</div>
            <div class="distribution-percentage">{{ ((reportData.distribution?.manual?.percentage || 0) * 100).toFixed(2) }}%</div>
          </div>
          <div class="distribution-card">
            <div class="distribution-label">綠界扣款</div>
            <div class="distribution-value">{{ reportData.distribution?.ecpay?.count || 0 }} 筆</div>
            <div class="distribution-amount">NT$ {{ formatCurrency(reportData.distribution?.ecpay?.totalAmount || 0) }}</div>
            <div class="distribution-percentage">{{ ((reportData.distribution?.ecpay?.percentage || 0) * 100).toFixed(2) }}%</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <p>請選擇報表類型並設定查詢條件</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { billingService } from '@/api/modules/billing.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import authService from '@/helpers/authservice/authService.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'FinancialReports',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('財務報表');

    const loading = ref(false);
    const error = ref(null);
    const reportData = ref(null);
    const selectedReportType = ref('');
    const platforms = ref([]);

    const filters = ref({
      startDate: '',
      endDate: '',
      date: new Date().toISOString().substring(0, 10),
      platformId: ''
    });

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const needsDateRange = computed(() => {
      return ['monthly_revenue', 'renewal_rate', 'payment_method_distribution'].includes(selectedReportType.value);
    });

    const needsSingleDate = computed(() => {
      return ['payment_status', 'aging_analysis'].includes(selectedReportType.value);
    });

    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) return;
      try {
        const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
        if (response.success) {
          platforms.value = (response.data.items || []).map(platform => ({
            id: platform.id,
            name: platform.platformName
          }));
        }
      } catch (err) {
        console.error('取得平台列表失敗:', err);
      }
    };

    const fetchReport = async () => {
      if (!selectedReportType.value) {
        showError('請選擇報表類型');
        return;
      }

      loading.value = true;
      error.value = null;
      reportData.value = null;

      try {
        let response;
        let params = {};

        // 根據報表類型構建參數
        switch (selectedReportType.value) {
          case 'monthly_revenue':
            if (!filters.value.startDate || !filters.value.endDate) {
              showError('請選擇開始日期和結束日期');
              loading.value = false;
              return;
            }
            params = {
              startDate: filters.value.startDate,
              endDate: filters.value.endDate
            };
            // 系統帳號可以傳遞 platformId
            if (isSystemAccount.value && filters.value.platformId) {
              params.platformId = filters.value.platformId;
            }
            response = await billingService.getMonthlyRevenueReport(params);
            break;
          case 'payment_status':
            params = {};
            // 如果有 date，則傳遞
            if (filters.value.date) {
              params.date = filters.value.date;
            }
            // 系統帳號可以傳遞 platformId
            if (isSystemAccount.value && filters.value.platformId) {
              params.platformId = filters.value.platformId;
            }
            response = await billingService.getPaymentStatusReport(params);
            break;
          case 'renewal_rate':
            if (!filters.value.startDate || !filters.value.endDate) {
              showError('請選擇開始日期和結束日期');
              loading.value = false;
              return;
            }
            params = {
              startDate: filters.value.startDate,
              endDate: filters.value.endDate
            };
            // 系統帳號可以傳遞 platformId
            if (isSystemAccount.value && filters.value.platformId) {
              params.platformId = filters.value.platformId;
            }
            response = await billingService.getRenewalRateReport(params);
            break;
          case 'aging_analysis':
            params = {};
            // 如果有 date，則傳遞
            if (filters.value.date) {
              params.date = filters.value.date;
            }
            // 系統帳號可以傳遞 platformId
            if (isSystemAccount.value && filters.value.platformId) {
              params.platformId = filters.value.platformId;
            }
            response = await billingService.getAgingAnalysisReport(params);
            break;
          case 'payment_method_distribution':
            if (!filters.value.startDate || !filters.value.endDate) {
              showError('請選擇開始日期和結束日期');
              loading.value = false;
              return;
            }
            params = {
              startDate: filters.value.startDate,
              endDate: filters.value.endDate
            };
            // 系統帳號可以傳遞 platformId
            if (isSystemAccount.value && filters.value.platformId) {
              params.platformId = filters.value.platformId;
            }
            response = await billingService.getPaymentMethodDistributionReport(params);
            break;
        }

        if (response.success) {
          reportData.value = response.data;
        } else {
          error.value = response.message || '取得報表失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得報表失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleReportTypeChange = () => {
      reportData.value = null;
      error.value = null;
    };

    const handleFilterChange = () => {
      // 篩選條件變更時不自動查詢，需要點擊查詢按鈕
    };

    const exportToExcel = async () => {
      if (!selectedReportType.value || !reportData.value) {
        showError('請先查詢報表');
        return;
      }

      try {
        loading.value = true;
        const params = {
          reportType: selectedReportType.value,
          format: 'excel'
        };

        // 根據報表類型添加對應的參數
        switch (selectedReportType.value) {
          case 'monthly_revenue':
          case 'renewal_rate':
          case 'payment_method_distribution':
            if (filters.value.startDate) params.startDate = filters.value.startDate;
            if (filters.value.endDate) params.endDate = filters.value.endDate;
            break;
          case 'payment_status':
          case 'aging_analysis':
            if (filters.value.date) params.date = filters.value.date;
            break;
        }

        // 系統帳號可以傳遞 platformId
        if (isSystemAccount.value && filters.value.platformId) {
          params.platformId = filters.value.platformId;
        }

        const blob = await billingService.exportReport(params);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedReportType.value}-${filters.value.startDate || filters.value.date || 'report'}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('匯出成功', '報表已匯出為 Excel 檔案');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    const exportToPDF = async () => {
      if (!selectedReportType.value || !reportData.value) {
        showError('請先查詢報表');
        return;
      }

      showError('PDF 匯出功能尚未實作', 'PDF 匯出功能將在後續版本中提供');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return new Intl.NumberFormat('zh-TW').format(amount);
    };

    const handleBack = () => {
      router.push('/system/billing');
    };

    onMounted(() => {
      fetchPlatforms();
    });

    return {
      pageTitle,
      loading,
      error,
      reportData,
      selectedReportType,
      filters,
      platforms,
      isSystemAccount,
      needsDateRange,
      needsSingleDate,
      fetchReport,
      handleReportTypeChange,
      handleFilterChange,
      exportToExcel,
      exportToPDF,
      formatCurrency,
      handleBack
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';

.page-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.filter-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  align-items: end;
}

.filter-item {
  display: flex;
  flex-direction: column;
}

.filter-item label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.export-buttons {
  display: flex;
  gap: 8px;
}

.report-content {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
}

.report-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 24px;
}

.report-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
}

.summary-item {
  display: flex;
  align-items: center;
}

.summary-label {
  font-size: 14px;
  color: #6b7280;
  margin-right: 8px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.status-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.status-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
}

.status-value {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.status-amount {
  font-size: 18px;
  font-weight: 500;
  color: #3b82f6;
}

.aging-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.aging-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.aging-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
}

.aging-value {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.aging-amount {
  font-size: 18px;
  font-weight: 500;
  color: #3b82f6;
}

.distribution-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.distribution-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.distribution-label {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.distribution-value {
  font-size: 20px;
  color: #6b7280;
  margin-bottom: 8px;
}

.distribution-amount {
  font-size: 24px;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 8px;
}

.distribution-percentage {
  font-size: 18px;
  font-weight: 500;
  color: #059669;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 48px;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc2626;
  margin-bottom: 16px;
}
</style>
